/**
 * The e-mail cells an import will DROP: `parseEmail` hands on only an
 * address the rule accepts (`src/lib/email-rule.ts`), so a cell it refuses
 * leaves the guest without an address. The manual importer shows this before
 * the upload (BodyChecker, the downloadable error list); a lane without that
 * screen — the AI import — reports this summary instead.
 *
 * Deliberately WITHOUT the address: how many, and for the first few the
 * sheet row and the rule's reason. The summary is read by a language model.
 */
import { DataType } from "@/types/enums/DataType";
import { DataTypeDetectionService } from "@/lib/utils/import-dtype-detection";
import { emailRuleReason, type EmailRuleReason } from "@/lib/email-rule";
import type { ImportMapper } from "../types/ImportMapper";
import type { TableColumnData } from "../types/TableColumnData";

export type DroppedEmailCells = {
  /** Every e-mail cell that is dropped, over all mapped EMAIL columns. */
  count: number;
  /**
   * The first few, by sheet row: `row` is 1-based with the header as row 1
   * (the number the spreadsheet shows), `reason` the rule's verdict.
   */
  examples: Array<{ row: number; reason: EmailRuleReason }>;
};

export function collectDroppedEmailCells(
  mappingMap: Map<number, ImportMapper>,
  tableData: TableColumnData[],
  {
    maxExamples = 3,
    submitted = () => true,
  }: {
    maxExamples?: number;
    /**
     * Whether data row `rowIndex` (0-based, the index into a column's
     * `values`) is actually imported. A row the caller drops — e.g. skipped
     * as empty because the refused address was its only content — is not
     * imported "without an address" and is not counted. Default: every row.
     */
    submitted?: (rowIndex: number) => boolean;
  } = {},
): DroppedEmailCells {
  const hits: Array<{ row: number; reason: EmailRuleReason }> = [];
  for (const [index, mapper] of mappingMap.entries()) {
    const field = mapper.field;
    if (!field || field.dataType !== DataType.EMAIL) continue;
    const entry = tableData[index];
    if (!entry) continue;
    // A deliverable column default takes the place of a refused cell —
    // `parseEmail` falls back to it — so nothing is dropped then.
    if (
      typeof mapper.value === "string" &&
      DataTypeDetectionService.isValidEmailValue(mapper.value)
    ) {
      continue;
    }
    entry.values.forEach((raw, i) => {
      if (typeof raw !== "string") return;
      if (!submitted(i)) return;
      if (DataTypeDetectionService.isEmptyEmailCell(raw)) return;
      const reason = emailRuleReason(DataTypeDetectionService.cleanEmailValue(raw));
      if (reason === null) return;
      hits.push({ row: i + 2, reason }); // +1 header row, +1 for 1-based rows
    });
  }
  hits.sort((a, b) => a.row - b.row);
  return { count: hits.length, examples: hits.slice(0, Math.max(0, maxExamples)) };
}
